import React, { useState, useEffect } from 'react';
import apiService from '../../../../services/ApiService';
import { castToAppType, formatValueForDisplay, areValuesEqual } from '../../../ErpConnections/typeCasting';
import TailwindModal from '../../../Tailwind/TailwindModal';
import { cn } from '../../../../lib/utils';

export interface ErpBinding {
    id: number;
    type: string;
    value_id: number;
    key: string | null;
    connection_id: number;
    scenario_id: number;
    last_fetched_at: string | null;
    created_at: string;
}

export interface SalesforceBinding extends ErpBinding {
    type: 'salesforce';
    sf_object: string;
    sf_record_id: string;
    sf_field: string;
}

type AppFieldType = 'str' | 'int' | 'float' | 'datetime' | 'bool';

interface ErpBindingButtonProps {
    onClick: (binding: ErpBinding | null) => void;
    tooltipTitle?: string;
    fieldId?: number;
    bindingKey?: string;
    refreshTrigger?: number;
    currentValue?: any;
    appFieldType?: AppFieldType;
    onSyncApply?: (erpValue: any) => void;
}

export default function ErpBindingButton({
    onClick,
    tooltipTitle = 'ERP Binding',
    fieldId,
    bindingKey,
    refreshTrigger = 0,
    currentValue,
    appFieldType,
    onSyncApply,
}: ErpBindingButtonProps) {
    const [bindings, setBindings] = useState<ErpBinding[]>([]);
    const [syncModalOpen, setSyncModalOpen] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);
    const [erpValue, setErpValue] = useState<any>(null);
    const [syncError, setSyncError] = useState<string | null>(null);

    useEffect(() => {
        if (fieldId) fetchBindings();
    }, [fieldId, bindingKey, refreshTrigger]);

    const fetchBindings = async () => {
        try {
            const response = bindingKey
                ? await apiService.getErpBindingWithKey(fieldId!, bindingKey)
                : await apiService.getErpBinding(fieldId!);

            if (response && response.ok && response.status !== 204) {
                const data = await response.json();
                let bindingsArray = Array.isArray(data) ? data : [data];
                bindingsArray = bindingKey
                    ? bindingsArray.filter((b: ErpBinding) => b.key === bindingKey)
                    : bindingsArray.filter((b: ErpBinding) => b.key === null);
                setBindings(bindingsArray);
            } else {
                setBindings([]);
            }
        } catch (error) {
            console.error('Failed to fetch ERP bindings:', error);
            setBindings([]);
        }
    };

    const hasBinding = bindings.length > 0;

    const handleSync = async () => {
        if (!hasBinding) return;
        const binding = bindings[0];
        setSyncModalOpen(true);
        setSyncLoading(true);
        setSyncError(null);
        setErpValue(null);
        try {
            if (binding.type === 'salesforce') {
                const response = await apiService.getSalesforceBindingValue(binding.id, bindingKey);
                if (response && response.ok) {
                    const data = await response.json();
                    setErpValue(data.value);
                } else {
                    setSyncError('Failed to fetch ERP value');
                }
            } else {
                setSyncError(`Sync not supported for binding type: ${binding.type}`);
            }
        } catch (error) {
            console.error('Failed to fetch ERP value:', error);
            setSyncError('An error occurred while fetching the ERP value');
        } finally {
            setSyncLoading(false);
        }
    };

    const handleApplySync = () => {
        if (onSyncApply && erpValue !== null && erpValue !== undefined) {
            const castedValue = appFieldType ? castToAppType(erpValue, appFieldType) : erpValue;
            onSyncApply(castedValue);
        }
        setSyncModalOpen(false);
    };

    const valuesAreEqual = areValuesEqual(currentValue, erpValue, appFieldType);

    return (
        <div className="flex items-center">
            {/* Binding button */}
            <button
                type="button"
                title={hasBinding ? 'ERP Binding (configured)' : tooltipTitle}
                onClick={() => onClick(hasBinding ? bindings[0] : null)}
                className="h-[30px] w-[30px] flex items-center justify-center rounded-full hover:bg-black/[0.04] transition-colors focus:outline-none ml-1"
            >
                <span className={cn(
                    'material-symbols-outlined text-[20px] leading-none',
                    hasBinding ? 'text-black' : 'text-[#AAAAAA]'
                )}>
                    settings_input_component
                </span>
            </button>

            {/* Sync button (only when binding exists) */}
            {hasBinding && (
                <button
                    type="button"
                    title="Sync from ERP"
                    onClick={handleSync}
                    className="h-[30px] w-[30px] flex items-center justify-center rounded-full hover:bg-black/[0.04] transition-colors focus:outline-none ml-0.5"
                >
                    <span className="material-symbols-outlined text-[20px] leading-none text-black">sync</span>
                </button>
            )}

            {/* Sync Modal */}
            <TailwindModal
                isOpen={syncModalOpen}
                onClose={() => setSyncModalOpen(false)}
                title="Sync from ERP"
                size="sm"
                footer={
                    <>
                        <button
                            onClick={() => setSyncModalOpen(false)}
                            className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                        >
                            Cancel
                        </button>
                        {!syncLoading && !syncError && erpValue !== null && onSyncApply && (
                            <button
                                onClick={handleApplySync}
                                disabled={valuesAreEqual}
                                className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Apply ERP Value
                            </button>
                        )}
                    </>
                }
            >
                {syncLoading ? (
                    <div className="flex justify-center py-8">
                        <div className="w-8 h-8 border-2 border-slate-200 border-t-slate-700 rounded-full animate-spin" />
                    </div>
                ) : syncError ? (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                        {syncError}
                    </div>
                ) : (
                    <div className="flex flex-col gap-3">
                        <div className="flex gap-3 items-center">
                            <div className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1">Current Value</p>
                                <p className="text-sm text-slate-800 break-words">{formatValueForDisplay(currentValue, appFieldType)}</p>
                            </div>
                            <span className="material-symbols-outlined text-[24px] text-primary shrink-0">arrow_forward</span>
                            <div className="flex-1 p-3 bg-primary/5 border border-primary/30 rounded-lg">
                                <p className="text-[10px] font-semibold text-primary uppercase tracking-wider mb-1">ERP Value</p>
                                <p className="text-sm text-slate-800 break-words">{formatValueForDisplay(erpValue, appFieldType)}</p>
                            </div>
                        </div>
                        {!valuesAreEqual && erpValue !== null && (
                            <p className="text-xs text-slate-500 text-center">
                                Values are different. Click "Apply" to update the field with the ERP value.
                            </p>
                        )}
                        {valuesAreEqual && erpValue !== null && (
                            <p className="text-xs text-green-600 text-center">Values are already in sync.</p>
                        )}
                    </div>
                )}
            </TailwindModal>
        </div>
    );
}
